import React, { useEffect, useRef, useState } from "react";
import {
  Box, Grid, Typography, Paper, Stack, Popover, Button, TextField,
  ToggleButton, ToggleButtonGroup, Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, subDays, startOfMonth } from "date-fns";
import api from "../../api";
import KpiCards from "./KpiCards";
import EnrollmentOverviewChart from "./EnrollmentOverviewChart";
import WorksheetCompletionDonut from "./WorksheetCompletionDonut";
import CoursePlansList from "./CoursePlansList";
import RecentCoursesTable from "./RecentCoursesTable";
import ResourceAllocationCard from "./ResourceAllocationCard";
import CourseRevenueCard from "./CourseRevenueCard";
import QuickActions from "./QuickActions";
import WaitingListCard from "./WaitingListCard";
import PlatformSummary from "./PlatformSummary";
import RecentAppUsersTable from "./RecentAppUsersTable";
import PanelHeading from "./PanelHeading";
import MostUsedUsersBar from "./MostUsedUsersBar";
import LabelledBars from "./LabelledBars";
import AppAttentionStrip from "./AppAttentionStrip";
import RevenueOverviewCard from "./RevenueOverviewCard";
import UserActivityCard from "./UserActivityCard";
import CourseDeliveryPanel from "./CourseDeliveryPanel";
import DeliveryAttentionStrip from "./DeliveryAttentionStrip";
import WeeklyTimetablePanel from "./WeeklyTimetablePanel";
import { adminHasAnyPermission } from "../../utils/adminPermissions";

const CARD_SX = {
  p: 2,
  height: "100%",
  borderRadius: 3,
  boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
  border: "1px solid #eef0f4",
};

const toIso = (d) => (d ? format(d, "yyyy-MM-dd") : null);

const buildRangeParams = (range) => {
  const params = {};
  if (range.preset) params.preset = range.preset;
  if (range.from) params.from = toIso(range.from);
  if (range.to) params.to = toIso(range.to);
  return params;
};

const PRESETS = [
  { key: "7d", label: "Last 7 days", from: () => subDays(new Date(), 7) },
  { key: "30d", label: "Last 30 days", from: () => subDays(new Date(), 30) },
  { key: "month", label: "This month", from: () => startOfMonth(new Date()) },
  { key: "all", label: "All time", from: () => null },
];

const APP_USER_PLACEHOLDER_CARDS = [
  { label: "Total App Users", value: "—", delta: 0, delta_label: "" },
  { label: "Active Subscriptions", value: "—", delta: 0, delta_label: "" },
  { label: "Trial Users", value: "—", delta: 0, delta_label: "" },
  { label: "Payment Failures", value: "—", delta: 0, delta_label: "" },
];

const PANEL_HINTS = {
  platform: "Shows how many users are using iOS, Android, Web, or an unknown platform.",
  mostUsed: "Shows the users who open/use the app the most.",
  subscriptions: "Shows how many users are on Free, Trial, Paid, or Expired plans.",
  revenue: "Shows the total money earned and how revenue changes over time.",
  activity: "Shows user activity for the selected dates (the last 7 days when it is All time) — Active Users "
    + "who used the app, New Users who joined, Sessions (app visits), and Avg. Session (average time in the app). "
    + "Click Active Users or New Users to see who they are.",
};

const WEAK_TOPIC_MIN_STUDENTS = 3;

const MOBILE_USERS = "/admin/mobile-users";

const PLATFORM_LINK = {
  iOS: `${MOBILE_USERS}?platform=ios`,
  Android: `${MOBILE_USERS}?platform=android`,
  Web: `${MOBILE_USERS}?platform=web`,
  Unknown: `${MOBILE_USERS}?platform=unknown`,
};

// state= uses each user's latest subscription, the same rule the segment counts with.
const SUBSCRIPTION_LINK = {
  Free: `${MOBILE_USERS}?state=free`,
  Trial: `${MOBILE_USERS}?state=trial`,
  Paid: `${MOBILE_USERS}?state=paid`,
  Expired: `${MOBILE_USERS}?state=expired`,
};

// The App users panels count people who joined in the dashboard range, so their links carry
// that range as the list's Joined from / to.
const withJoinedRange = (href, range) => {
  if (!href) return href;
  const preset = PRESETS.find((p) => p.key === range.preset);
  const from = preset ? toIso(preset.from()) : toIso(range.from);
  const to = preset ? (from ? toIso(new Date()) : null) : toIso(range.to);
  if (!from && !to) return href;
  const [path, query = ""] = href.split("?");
  const params = new URLSearchParams(query);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  return `${path}?${params.toString()}`;
};

// Admin and Super Admin always get the switch. Their permission list is written
// after login and can be missing on the first paint, which used to hide both tabs.
const canViewAppUsers = () => {
  const role = (localStorage.getItem("role") || "").toLowerCase().trim();
  if (role === "admin" || role === "super admin" || role === "super_admin") return true;
  return adminHasAnyPermission(["view-mobile-users", "view-parents-subscriptions"]);
};

// App users stays the default (29 Oct meeting): most of the business is the app.
// A Course tab chosen earlier is not restored.
const initialTab = (showAppUsers) => (showAppUsers ? "appUsers" : "course");

export default function AdminDashboard() {
  const showAppUsersTab = canViewAppUsers();
  const [tab, setTabState] = useState(() => initialTab(showAppUsersTab));
  const tabPicked = useRef(false);
  const setTab = (next) => {
    tabPicked.current = true;
    setTabState(next);
  };
  // Permissions can arrive after the first paint. Open App Users unless Course was chosen.
  useEffect(() => {
    if (showAppUsersTab && !tabPicked.current) setTabState("appUsers");
  }, [showAppUsersTab]);
  const [range, setRange] = useState({ from: null, to: null, preset: null });
  const [pendingRange, setPendingRange] = useState({ from: null, to: null, preset: null });
  const [popAnchor, setPopAnchor] = useState(null);
  const popOpen = Boolean(popAnchor);

  const [stats, setStats] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [plans, setPlans] = useState([]);
  const [recent, setRecent] = useState([]);
  const [summary, setSummary] = useState(null);
  // Delivery state is "right now", not a date range — see the effect below.
  const [capacity, setCapacity] = useState(null);
  const [slots, setSlots] = useState([]);
  const [weakTopics, setWeakTopics] = useState(0);
  const [appUsers, setAppUsers] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = buildRangeParams(range);
    Promise.allSettled([
      api.get("admin/dashboard/stats", { params }),
      api.get("admin/dashboard/enrollment-overview", { params }),
      api.get("admin/dashboard/worksheet-completion", { params }),
      api.get("admin/dashboard/course-plans", { params }),
      api.get("admin/dashboard/recent-courses", { params }),
      api.get("admin/dashboard/platform-summary"),
    ])
      .then((results) => {
        if (cancelled) return;
        const [s, e, w, p, r, sm] = results;
        if (s.status === "fulfilled") setStats(s.value.data);
        if (e.status === "fulfilled") setEnrollment(e.value.data);
        if (w.status === "fulfilled") setCompletion(w.value.data);
        if (p.status === "fulfilled") setPlans(p.value.data?.plans || []);
        if (r.status === "fulfilled") setRecent(r.value.data?.courses || []);
        if (sm.status === "fulfilled") setSummary(sm.value.data);
      });
    return () => { cancelled = true; };
  }, [range.from, range.to, range.preset]);

  // Group staffing, seats and the weekly timetable are current-state: neither endpoint accepts a
  // date window, so this runs once rather than on every range change. Both are optional — a panel
  // simply does not render when its request fails, which is also what an admin without the
  // permission sees.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.get("admin/analytics/batch-capacity"),
      api.get("admin/course/timeslot"),
      // min_students=3 so the strip only nags about topics with enough students to be worth a
      // class — a one-student cohort is a conversation, not a session to schedule.
      api.get("admin/analytics/topic-cohorts", { params: { min_students: WEAK_TOPIC_MIN_STUDENTS } }),
    ]).then(([cap, ts, wt]) => {
      if (cancelled) return;
      setCapacity(cap.status === "fulfilled" ? (cap.value.data?.data?.rows || []) : null);
      setSlots(ts.status === "fulfilled" && Array.isArray(ts.value.data?.data) ? ts.value.data.data : []);
      setWeakTopics(wt.status === "fulfilled" ? (wt.value.data?.data?.cohorts || []).length : 0);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (tab !== "appUsers" || !showAppUsersTab) return undefined;
    let cancelled = false;
    api.get("admin/dashboard/app-users", { params: buildRangeParams(range) })
      .then((res) => { if (!cancelled) setAppUsers(res.data); })
      .catch(() => { if (!cancelled) setAppUsers(null); });
    return () => { cancelled = true; };
  }, [tab, showAppUsersTab, range.from, range.to, range.preset]);

  const openPopover = (event) => {
    setPendingRange(range);
    setPopAnchor(event.currentTarget);
  };
  const closePopover = () => setPopAnchor(null);
  const applyRange = () => {
    setRange({ ...pendingRange, preset: null });
    closePopover();
  };
  const applyPreset = (preset) => {
    const from = preset.from();
    const next = from
      ? { from, to: new Date(), preset: preset.key }
      : { from: null, to: null, preset: null };
    setPendingRange(next);
    setRange(next);
    closePopover();
  };

  const rangeLabel = range.preset
    ? (PRESETS.find((p) => p.key === range.preset)?.label || "All time")
    : (range.from && range.to)
      ? `${format(range.from, "d MMM yyyy")} — ${format(range.to, "d MMM yyyy")}`
      : "All time";

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mb={2}
          sx={{ flexWrap: "wrap" }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: "#111827" }}>Dashboard</Typography>
            <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>Overview of your platform activities</Typography>
          </Box>
          {showAppUsersTab && (
            <ToggleButtonGroup
              value={tab}
              exclusive
              onChange={(_, next) => next && setTab(next)}
              sx={{
                bgcolor: "#f1f2f6",
                borderRadius: 999,
                p: 0.5,
                flexShrink: 0,
                alignSelf: { xs: "flex-start", sm: "center" },
                "& .MuiToggleButton-root": {
                  border: 0,
                  borderRadius: "999px !important",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 13,
                  px: 2.5,
                  height: 36,
                  color: "#6b7280",
                },
                "& .Mui-selected": {
                  bgcolor: "#fff !important",
                  color: "#111827 !important",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                },
              }}
            >
              <ToggleButton value="appUsers">App Users</ToggleButton>
              <ToggleButton value="course">Course</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button
            onClick={openPopover}
            startIcon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
            endIcon={<ExpandMoreIcon />}
            variant="outlined"
            sx={{
              bgcolor: "white",
              borderColor: "#e5e7eb",
              color: "text.primary",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              height: 40,
              px: 1.5,
              "&:hover": { bgcolor: "#f9fafb", borderColor: "#d1d5db" },
            }}
          >
            {rangeLabel}
          </Button>
          <Popover
            open={popOpen}
            anchorEl={popAnchor}
            onClose={closePopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { p: 2, mt: 1 } }}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {PRESETS.map((p) => {
                  const selected = p.key === "all" ? !range.preset && !range.from : range.preset === p.key;
                  return (
                    <Button
                      key={p.key}
                      size="small"
                      onClick={() => applyPreset(p)}
                      variant={selected ? "contained" : "outlined"}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                        ...(selected
                          ? { bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" } }
                          : { borderColor: "#e5e7eb", color: "#374151" }),
                      }}
                    >
                      {p.label}
                    </Button>
                  );
                })}
              </Stack>
              <DatePicker
                label="From"
                value={pendingRange.from}
                onChange={(d) => setPendingRange((p) => ({ ...p, from: d }))}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
              <DatePicker
                label="To"
                value={pendingRange.to}
                onChange={(d) => setPendingRange((p) => ({ ...p, to: d }))}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={closePopover}>Cancel</Button>
                <Button size="small" variant="contained" onClick={applyRange} sx={{ bgcolor: "#7c3aed" }}>
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Popover>
        </Stack>

        {tab === "course" && (
        <>
        <KpiCards cards={stats?.cards} />

        <Box mt={2}>
          <DeliveryAttentionStrip capacity={capacity} weakTopics={weakTopics} />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <CourseDeliveryPanel plans={plans} capacity={capacity} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <WeeklyTimetablePanel slots={slots} />
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} md={6} lg={5}>
            <Paper sx={CARD_SX}>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#111827" }}>Enrollment Overview</Typography>
              <Box sx={{ position: "relative", minHeight: { xs: 200, sm: 240 }, width: "100%" }}>
                <EnrollmentOverviewChart data={enrollment} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={CARD_SX}>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#111827" }}>Worksheet Completion</Typography>
              <Box sx={{ position: "relative", minHeight: { xs: 160, sm: 180 }, width: "100%" }}>
                <WorksheetCompletionDonut data={completion} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={3}>
            <Paper sx={CARD_SX}>
              <CoursePlansList plans={plans} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={5}>
            <Paper sx={CARD_SX}>
              <RecentCoursesTable rows={recent} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <ResourceAllocationCard />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={CARD_SX}>
              <CourseRevenueCard params={buildRangeParams(range)} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={CARD_SX}>
              <WaitingListCard />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={3}>
            <Paper sx={CARD_SX}>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#111827" }}>Quick Actions</Typography>
              <QuickActions />
              <Box mt={2}>
                <PlatformSummary summary={summary} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
        </>
        )}

        {tab === "appUsers" && showAppUsersTab && (
        <>
        <KpiCards cards={appUsers?.cards || APP_USER_PLACEHOLDER_CARDS} />

        <AppAttentionStrip
          cards={appUsers?.cards}
          subscriptions={appUsers?.subscription_overview}
          linkFor={(label) => withJoinedRange(SUBSCRIPTION_LINK[label], range)}
        />

        {/* Money → activity → people: wide panels left, breakdowns right (design item 27). */}
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} lg={8}>
            <Paper sx={CARD_SX}>
              <RevenueOverviewCard hint={PANEL_HINTS.revenue} params={buildRangeParams(range)} rangeLabel={rangeLabel} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={CARD_SX}>
              <PanelHeading title="Subscriptions" hint={PANEL_HINTS.subscriptions} scope={rangeLabel} />
              <LabelledBars
                segments={appUsers?.subscription_overview}
                colors={{ Expired: "#B91C1C", Free: "#6B7280", Paid: "#15803D", Trial: "#B45309" }}
                linkFor={(label) => withJoinedRange(SUBSCRIPTION_LINK[label], range)}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Paper sx={CARD_SX}>
              <PanelHeading
                title="User Activity"
                hint={PANEL_HINTS.activity}
                scope={range.preset || (range.from && range.to) ? rangeLabel : "Last 7 days"}
              />
              <UserActivityCard activity={appUsers?.activity || appUsers?.activity_7d} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={CARD_SX}>
              <PanelHeading title="Platform" hint={PANEL_HINTS.platform} scope={rangeLabel} />
              <LabelledBars
                segments={appUsers?.platform_split}
                colors={{ Unknown: "#9CA3AF" }}
                linkFor={(label) => withJoinedRange(PLATFORM_LINK[label], range)}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Paper sx={CARD_SX}>
              <RecentAppUsersTable rows={appUsers?.recent} />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper sx={CARD_SX}>
              <PanelHeading
                title="Most Used Users"
                hint={PANEL_HINTS.mostUsed}
                scope={rangeLabel}
                action={(
                  <MuiLink
                    component={RouterLink}
                    to={`${MOBILE_USERS}?sort=opens`}
                    underline="hover"
                    sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 600 }}
                  >
                    View all
                  </MuiLink>
                )}
              />
              <MostUsedUsersBar rows={appUsers?.most_used_users} />
            </Paper>
          </Grid>
        </Grid>
        </>
        )}
      </Box>
    </LocalizationProvider>
  );
}
